import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Upload, ExternalLink } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Reads a File and returns a resized JPEG data-URL.
 * Keeps the resulting size < ~120 KB for Mongo storage.
 */
const resizeToDataURL = (file, maxW, maxH) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('image failed'));
      img.onload = () => {
        const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const ImageUploadField = ({ label, value, onChange, testId, aspect = '1:1' }) => {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Selecciona una imagen');
    if (file.size > 5 * 1024 * 1024) return toast.error('La imagen debe pesar menos de 5 MB');
    setBusy(true);
    try {
      const [maxW, maxH] = aspect === 'cover' ? [1200, 400] : [400, 400];
      const dataUrl = await resizeToDataURL(file, maxW, maxH);
      onChange(dataUrl);
    } catch (err) {
      toast.error('No se pudo procesar la imagen');
    } finally {
      setBusy(false);
      // Reset input so re-selecting the same file still triggers onChange
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className={`relative rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 overflow-hidden ${aspect === 'cover' ? 'aspect-[3/1]' : 'aspect-square max-w-[160px]'}`}>
        {value ? (
          <img src={value} alt="preview" className="absolute inset-0 w-full h-full object-cover" data-testid={`${testId}-preview`} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-xs">
            {aspect === 'cover' ? 'Sin foto de portada' : 'Sin logo'}
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFile}
          data-testid={`${testId}-input`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          data-testid={`${testId}-upload-btn`}
        >
          <Upload size={14} className="mr-1" /> {busy ? 'Procesando…' : 'Subir'}
        </Button>
        {value && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)} className="text-red-600">
            Quitar
          </Button>
        )}
      </div>
    </div>
  );
};

const SellerProfileEditor = ({ open, onOpenChange }) => {
  const { user, getAuthHeader, refreshUser } = useAuth();
  const [form, setForm] = useState({
    description: '',
    website: '',
    hours: '',
    logo_url: '',
    cover_url: '',
    catalog_delivery_mode: 'either',
    catalog_delivery_fee: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && open) {
      setForm({
        description: user.description || '',
        website: user.website || '',
        hours: user.hours || '',
        logo_url: user.logo_url || '',
        cover_url: user.cover_url || '',
        catalog_delivery_mode: user.catalog_delivery_mode || 'either',
        catalog_delivery_fee: user.catalog_delivery_fee ? String(user.catalog_delivery_fee) : '',
      });
    }
  }, [user, open]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/users/me`,
        {
          description: form.description || null,
          website: form.website || null,
          hours: form.hours || null,
          logo_url: form.logo_url || null,
          cover_url: form.cover_url || null,
          catalog_delivery_mode: form.catalog_delivery_mode,
          catalog_delivery_fee: parseFloat(form.catalog_delivery_fee) || 0,
        },
        { headers: { Authorization: getAuthHeader() } }
      );
      await refreshUser();
      toast.success('Perfil público actualizado');
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = user ? `${window.location.origin}/proveedor/${user.id}` : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="seller-profile-editor" className="max-h-[90vh] overflow-y-auto max-w-lg">
        <DialogHeader>
          <DialogTitle>Perfil público</DialogTitle>
          <DialogDescription>
            Personaliza cómo te ven los compradores. Este perfil es visible sin necesidad de iniciar sesión.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <ImageUploadField
            label="Foto de portada"
            value={form.cover_url}
            onChange={(v) => setForm({ ...form, cover_url: v })}
            testId="cover-image"
            aspect="cover"
          />
          <ImageUploadField
            label="Logotipo"
            value={form.logo_url}
            onChange={(v) => setForm({ ...form, logo_url: v })}
            testId="logo-image"
          />

          <div className="space-y-2">
            <Label>Descripción</Label>
            <Textarea
              data-testid="profile-description-input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Cuéntales quién eres, qué productos ofreces y qué te diferencia..."
              rows={5}
              maxLength={800}
            />
            <p className="text-xs text-slate-400 text-right">{form.description.length} / 800</p>
          </div>

          <div className="space-y-2">
            <Label>Sitio web</Label>
            <Input
              data-testid="profile-website-input"
              type="url"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://tuempresa.com"
            />
          </div>

          <div className="space-y-2">
            <Label>Horario de atención</Label>
            <Textarea
              data-testid="profile-hours-input"
              value={form.hours}
              onChange={(e) => setForm({ ...form, hours: e.target.value })}
              placeholder="Lun-Vie: 9h-14h y 16h-19h&#10;Sáb: 10h-13h"
              rows={3}
              maxLength={300}
            />
          </div>

          <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="catalog-delivery-settings">
            <p className="text-xs font-semibold text-slate-700">Entrega de pedidos de catálogo</p>
            <div className="space-y-1">
              <Label className="text-xs">Modo de entrega</Label>
              <Select value={form.catalog_delivery_mode} onValueChange={(v) => setForm({ ...form, catalog_delivery_mode: v })}>
                <SelectTrigger data-testid="catalog-delivery-mode-select" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="either">Flexible (el comprador elige)</SelectItem>
                  <SelectItem value="delivery">Solo entrega a domicilio</SelectItem>
                  <SelectItem value="pickup">Solo recogida</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.catalog_delivery_mode !== 'pickup' && (
              <div className="space-y-1">
                <Label className="text-xs">Coste de transporte por pedido (€)</Label>
                <Input
                  data-testid="catalog-delivery-fee-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.catalog_delivery_fee}
                  onChange={(e) => setForm({ ...form, catalog_delivery_fee: e.target.value })}
                  placeholder="0.00"
                  className="h-9"
                />
                <p className="text-[11px] text-slate-500">Se suma al pedido solo cuando la entrega es a domicilio.</p>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs font-medium text-slate-700 mb-1">Enlace público</p>
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="profile-public-link"
              className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline break-all"
            >
              {publicUrl}
              <ExternalLink size={12} />
            </a>
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1" disabled={saving}>
            Cancelar
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="profile-save-button"
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SellerProfileEditor;
